import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { 
  Network, 
  Server, 
  Wifi, 
  AlertTriangle, 
  Shield, 
  RefreshCw, 
  BarChart,
  Clock,
  CheckCircle2,
  XCircle
} from "lucide-react";
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';

type AutomationType = 'network' | 'device' | 'security' | 'deployment';

type AutomationSetting = {
  id: string;
  name: string;
  description: string;
  type: AutomationType;
  enabled: boolean;
  icon: React.ElementType;
  lastRun?: string;
  nextRun?: string;
  status?: 'success' | 'warning' | 'error' | 'idle';
};

interface AutomationControlProps {
  initialTask?: string | null;
}

const defaultAutomations: AutomationSetting[] = [
  {
    id: 'auto-1',
    name: 'Network Optimization',
    description: 'Automatically optimize network routing and load balancing',
    type: 'network',
    enabled: true,
    icon: Network,
    lastRun: '2 hours ago',
    nextRun: 'In 4 hours',
    status: 'success'
  },
  {
    id: 'auto-2',
    name: 'Security Scans',
    description: 'Run periodic security vulnerability scans',
    type: 'security',
    enabled: true,
    icon: Shield,
    lastRun: '6 hours ago',
    nextRun: 'In 18 hours',
    status: 'success'
  },
  {
    id: 'auto-3',
    name: 'Device Updates',
    description: 'Auto-deploy firmware and software updates to devices',
    type: 'device',
    enabled: false,
    icon: Wifi,
    lastRun: '3 days ago',
    nextRun: 'Disabled',
    status: 'idle'
  },
  {
    id: 'auto-4',
    name: 'Threat Response',
    description: 'Automatically respond to detected security threats',
    type: 'security',
    enabled: true,
    icon: AlertTriangle,
    lastRun: 'Never',
    nextRun: 'On demand',
    status: 'idle'
  },
  {
    id: 'auto-5',
    name: 'VM Scaling',
    description: 'Scale VM resources based on demand',
    type: 'deployment',
    enabled: true,
    icon: Server,
    lastRun: '1 day ago',
    nextRun: 'On demand',
    status: 'success'
  },
  {
    id: 'auto-6',
    name: 'Performance Monitoring',
    description: 'Continuously monitor and report on network performance',
    type: 'network',
    enabled: true,
    icon: BarChart,
    lastRun: 'Ongoing',
    nextRun: 'Real-time',
    status: 'success'
  },
  {
    id: 'auto-7',
    name: 'Backup Configuration',
    description: 'Automated backup of all network and device configurations',
    type: 'deployment',
    enabled: true,
    icon: RefreshCw,
    lastRun: '12 hours ago',
    nextRun: 'In 12 hours',
    status: 'success'
  },
  {
    id: 'auto-8',
    name: 'Mesh Self-Healing',
    description: 'Automatically repair mesh network connections',
    type: 'network',
    enabled: true,
    icon: RefreshCw,
    lastRun: '3 hours ago',
    nextRun: 'On demand',
    status: 'warning'
  },
];

const AutomationControl: React.FC<AutomationControlProps> = ({ initialTask }) => {
  const [automations, setAutomations] = useState<AutomationSetting[]>(defaultAutomations);
  const [masterSwitch, setMasterSwitch] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('all');
  
  // Process initialTask when component mounts
  useEffect(() => {
    if (initialTask) {
      const matchingAutomation = automations.find(
        auto => auto.name.toLowerCase().includes(initialTask.toLowerCase())
      );
      
      if (matchingAutomation) {
        handleRunNow(matchingAutomation.id);
        // Set the appropriate tab
        setActiveTab(matchingAutomation.type);
      } else {
        toast.info(`Creating custom task: ${initialTask}`, {
          description: "Analyzing requirements and configuring automation task."
        });
      }
    }
  }, [initialTask]);
  
  const handleToggleAutomation = (id: string) => {
    setAutomations(prev => 
      prev.map(auto => 
        auto.id === id 
          ? { ...auto, enabled: !auto.enabled } 
          : auto
      )
    );
    
    const automation = automations.find(auto => auto.id === id);
    if (automation) {
      toast(
        automation.enabled ? 'Automation disabled' : 'Automation enabled',
        {
          description: `${automation.name} has been ${automation.enabled ? 'disabled' : 'enabled'}.`,
        }
      );
    }
  };
  
  const handleMasterToggle = () => {
    const newState = !masterSwitch;
    setMasterSwitch(newState);
    setAutomations(prev => 
      prev.map(auto => ({ ...auto, enabled: newState }))
    );
    
    toast(
      newState ? 'AI Automation enabled' : 'AI Automation disabled',
      {
        description: `All automation features have been ${newState ? 'enabled' : 'disabled'}.`,
      }
    );
  };
  
  const handleRunNow = (id: string) => {
    toast.success('Automation triggered', {
      description: 'The automation task has been manually triggered and is now running.',
    });
    
    // Update the automation's lastRun
    setAutomations(prev => 
      prev.map(auto => 
        auto.id === id 
          ? { ...auto, lastRun: 'Just now', status: 'success' as const } 
          : auto
      )
    );
  };
  
  const filteredAutomations = activeTab === 'all' 
    ? automations 
    : automations.filter(auto => auto.type === activeTab);
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span>AI Automation Master Control</span>
            <div className="flex items-center space-x-2">
              <Label htmlFor="master-ai-toggle" className="text-sm font-normal">
                {masterSwitch ? 'Enabled' : 'Disabled'}
              </Label>
              <Switch 
                id="master-ai-toggle" 
                checked={masterSwitch}
                onCheckedChange={handleMasterToggle}
              />
            </div>
          </CardTitle>
          <CardDescription>
            Enable or disable all AI automation features for your network infrastructure
          </CardDescription>
        </CardHeader>
      </Card>
      
      <div>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 grid grid-cols-5 w-full md:w-auto">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="network">Network</TabsTrigger>
            <TabsTrigger value="device">Device</TabsTrigger>
            <TabsTrigger value="security">Security</TabsTrigger>
            <TabsTrigger value="deployment">Deployment</TabsTrigger>
          </TabsList>
          
          <TabsContent value={activeTab} className="mt-0">
            <div className="grid gap-4 md:grid-cols-2">
              {filteredAutomations.map((automation) => (
                <Card key={automation.id}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex justify-between items-center">
                      <div className="flex items-center">
                        <automation.icon className="w-5 h-5 mr-2" />
                        {automation.name}
                      </div>
                      <Switch 
                        checked={masterSwitch && automation.enabled}
                        onCheckedChange={() => handleToggleAutomation(automation.id)}
                        disabled={!masterSwitch}
                      />
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {automation.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pb-2 pt-0">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center">
                        <Clock className="w-3 h-3 mr-1 text-muted-foreground" />
                        <span className="text-muted-foreground">Last run:</span>
                      </div>
                      <div>{automation.lastRun}</div>
                      
                      <div className="flex items-center">
                        <Clock className="w-3 h-3 mr-1 text-muted-foreground" />
                        <span className="text-muted-foreground">Next run:</span>
                      </div>
                      <div>{automation.nextRun}</div>
                      
                      <div className="flex items-center">
                        <span className="text-muted-foreground mr-1">Status:</span>
                      </div>
                      <div className="flex items-center">
                        {automation.status === 'success' && <CheckCircle2 className="w-3 h-3 mr-1 text-green-500" />}
                        {automation.status === 'warning' && <AlertTriangle className="w-3 h-3 mr-1 text-yellow-500" />}
                        {automation.status === 'error' && <XCircle className="w-3 h-3 mr-1 text-red-500" />}
                        {automation.status === 'idle' && <Clock className="w-3 h-3 mr-1 text-muted-foreground" />}
                        <span className={
                          automation.status === 'success' ? 'text-green-500' :
                          automation.status === 'warning' ? 'text-yellow-500' :
                          automation.status === 'error' ? 'text-red-500' :
                          'text-muted-foreground'
                        }>
                          {automation.status === 'success' ? 'Healthy' :
                           automation.status === 'warning' ? 'Warning' :
                           automation.status === 'error' ? 'Error' :
                           'Idle'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                  
                  <CardFooter className="pt-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="text-xs h-8 w-full"
                      onClick={() => handleRunNow(automation.id)}
                      disabled={!masterSwitch || !automation.enabled}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Run Now
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AutomationControl;
